'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

export default function PrinterModelPage() {
  const [searchValue, setSearchValue] = useState('');
  const [checked, setChecked] = useState(false);
  const router = useRouter();
  const supabase = createClient();

  useEffect(() => {
    const checkSwap = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('printer_model_buy_swapped')
        .eq('id', 'global')
        .single();
      if (data?.printer_model_buy_swapped) {
        router.replace('/buy');
      } else {
        setChecked(true);
      }
    };
    checkSwap();
  }, []);

  if (!checked) return null;

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchValue.trim() !== '') {
      router.push('/download');
    }
  };

  const handleStart = () => {
    router.push('/download');
  };

  return (
    <>
      <style>{`
        .pm-root {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          background-color: #FFFFFF;
          font-family: Verdana, Geneva, sans-serif;
          max-width: 480px;
          margin: 0 auto;
          width: 100%;
        }
        .pm-banner { width: 100%; line-height: 0; }
        .pm-banner img { width: 100%; height: auto; display: block; }
        .pm-body { background-color: #2C2B2B; flex: 1; }
        .pm-inner {
          padding: 28px 20px 40px 20px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .pm-printer-img { margin-bottom: 24px; width: 100%; display: flex; justify-content: center; }
        .pm-printer-img img { width: 200px; height: auto; object-fit: contain; }
        .pm-heading {
          font-family: Verdana, Geneva, sans-serif;
          font-weight: 800;
          font-size: 24px;
          color: #FFFFFF;
          line-height: 1.3;
          margin: 0 0 14px 0;
          text-align: center;
          letter-spacing: -0.3px;
        }
        .pm-desc {
          font-family: Verdana, Geneva, sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #E0E0E0;
          line-height: 1.7;
          margin: 0 0 28px 0;
          text-align: center;
        }
        .pm-input {
          width: 100%;
          padding: 18px 20px;
          font-size: 18px;
          font-weight: 700;
          border: 3px solid #ccc;
          border-radius: 12px;
          outline: none;
          background-color: #FFFFFF;
          color: #333;
          font-family: Verdana, Geneva, sans-serif;
          box-sizing: border-box;
          margin-bottom: 16px;
        }
        .pm-btn {
          width: 100%;
          background-color: #C00101;
          color: #FFFFFF;
          font-family: Verdana, Geneva, sans-serif;
          font-size: 20px;
          font-weight: 800;
          letter-spacing: 1.5px;
          padding: 20px 28px;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          text-transform: uppercase;
          box-shadow: 0 4px 16px rgba(192,1,1,0.4);
        }
        .pm-footer {
          background-color: #FFFFFF;
          border-top: 1px solid #D1D5DB;
          padding: 18px 20px;
          text-align: center;
        }
        .pm-footer p {
          color: #334155;
          font-size: 13px;
          font-family: Verdana, Geneva, sans-serif;
          margin: 0;
        }
        .pm-desktop-info { display: none; }

        /* ── DESKTOP ── */
        @media (min-width: 1024px) {
          .pm-root { max-width: 100%; margin: 0; }
          .pm-banner img { max-height: 100px; object-fit: cover; object-position: center; width: 100%; }
          .pm-body {
            display: flex;
            align-items: stretch;
            flex: 1;
          }
          .pm-inner {
            max-width: 1280px;
            margin: 0 auto;
            width: 100%;
            flex-direction: row;
            align-items: stretch;
            gap: 100px;
            padding: 70px 100px;
            flex: 1;
          }
          .pm-left-col {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .pm-right-col {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
          }
          .pm-printer-img { margin-bottom: 0; }
          .pm-printer-img img { width: 360px; filter: drop-shadow(0 20px 40px rgba(0,0,0,0.5)); }
          .pm-heading {
            font-size: 40px;
            text-align: left;
            margin-bottom: 16px;
          }
          .pm-desc {
            font-size: 17px;
            text-align: left;
            margin-bottom: 32px;
          }
          .pm-input {
            font-size: 20px;
            padding: 22px 24px;
            margin-bottom: 20px;
            border-radius: 14px;
          }
          .pm-btn {
            font-size: 22px;
            padding: 24px 40px;
            border-radius: 14px;
          }
          .pm-desktop-info {
            display: flex;
            flex-direction: column;
            gap: 14px;
            margin-top: 36px;
            width: 100%;
          }
          .pm-info-item {
            display: flex;
            align-items: flex-start;
            gap: 14px;
            background-color: rgba(255,255,255,0.07);
            border-radius: 12px;
            padding: 16px 18px;
          }
          .pm-info-icon { font-size: 22px; flex-shrink: 0; margin-top: 2px; }
          .pm-info-text {
            font-family: Verdana, Geneva, sans-serif;
            font-size: 14px;
            font-weight: 700;
            color: #FFFFFF;
            margin: 0;
            line-height: 1.5;
          }
          .pm-info-text span {
            display: block;
            font-size: 13px;
            font-weight: 500;
            color: rgba(255,255,255,0.6);
            margin-top: 3px;
          }
          .pm-footer p { font-size: 14px; }
        }
      `}</style>

      <div className="pm-root">
        {/* Hero Banner */}
        <div className="pm-banner">
          <img
            src="https://ij.startcanonn.com/wp-content/uploads/2026/04/image_2026-04-22_194305185.png"
            alt="Canon banner"
          />
        </div>

        {/* Main Content */}
        <div className="pm-body">
          <div className="pm-inner">
            {/* Left col (printer image) */}
            <div className="pm-left-col">
              <div className="pm-printer-img">
                <img
                  src="https://ij.startcanonn.com/wp-content/uploads/2026/04/printer-new.png"
                  alt="Canon Printer"
                />
              </div>
            </div>

            {/* Right col (form) */}
            <div className="pm-right-col">
              <h2 className="pm-heading">Setup Your Canon Printer</h2>
              <p className="pm-desc">
                Enter your Canon product name and model number to download the correct printer software and drivers.
              </p>
              <input
                type="text"
                placeholder="Eg., TS3520"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pm-input"
              />
              <button onClick={handleStart} className="pm-btn">
                Start Setup
              </button>

              {/* Desktop info items */}
              <div className="pm-desktop-info">
                <div className="pm-info-item">
                  <span className="pm-info-icon">🖨️</span>
                  <p className="pm-info-text">Supports All Canon Models
                    <span>PIXMA, MAXIFY, imageCLASS, SELPHY and more</span>
                  </p>
                </div>
                <div className="pm-info-item">
                  <span className="pm-info-icon">💻</span>
                  <p className="pm-info-text">Windows &amp; Mac Compatible
                    <span>Works with Windows 10/11 and macOS 12 or later</span>
                  </p>
                </div>
                <div className="pm-info-item">
                  <span className="pm-info-icon">🛡️</span>
                  <p className="pm-info-text">Official Canon Software
                    <span>Verified and certified drivers from Canon</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pm-footer">
          <p>Copyright &copy; 2025 ij.startcanonn.com</p>
        </div>
      </div>
    </>
  );
}
